import { createContext, useContext, useEffect, useState } from 'react';
import { DataStore, Hub, syncExpression } from 'aws-amplify';
import { addBreadcrumb, captureError, captureMessage } from '../common/sentry';
import { UserDetailsDataProps, userDetailsData } from '../datastore/userDetails';
import { SSCCsDataProps, ssccsData } from '../datastore/ssccs';
import { LocationDataProps, locationData } from '../datastore/locations';
import { PrinterDataProps, printersData } from '../datastore/printers';
import { PackageTypeDataProps, packageTypeData } from '../datastore/packageType';
import { QMReasonDataProps, qmReasonData } from '../datastore/qmReason';
import { QMIssueDataProps, qmIssueData } from '../datastore/qmIssue';
import { WorkflowStatusDataProps, workflowStatusData } from '../datastore/workflowStatus';
import { TripSheetsDataProps, tripSheetsData } from '../datastore/tripSheets';
import { RemoveItemSSCCHistoryDataProps, removeItemSSCCHistory } from '../datastore/removeItemSSCCHistory';
import { ReceiptEventPackageHistoryDataProps, receiptEventPackageHistory } from '../datastore/receiptEventHistory';
import { AdminActionHistoryDataProps, adminActionHistory } from '../datastore/adminActionHistory';
import { TransportBookingEventDataProps, transportBookingEventData } from '../datastore/transportBookingEvent';
import {
  AdminActionHistory,
  ReceiptEventPackageHistory,
  RemoveItemSSCCHistory,
  SSCC,
  TripSheet,
  TransportBookingEvent
} from '../models';
import dayjs from 'dayjs';
import { SsccStatus } from '../common';

export interface DatastoreContextProps {
  isDatastoreReady: boolean;
  isSyncing: boolean;
  syncProgress: {
    [key: string]: { new: number; updated: number; deleted: number };
  };
  userDetails: UserDetailsDataProps;
  ssccs: SSCCsDataProps;
  tripSheets: TripSheetsDataProps;
  locations: LocationDataProps;
  printers: PrinterDataProps;
  packageTypes: PackageTypeDataProps;
  qmReasons: QMReasonDataProps;
  qmIssues: QMIssueDataProps;
  removeItemSSCCHistories: RemoveItemSSCCHistoryDataProps;
  workflowStatuses: WorkflowStatusDataProps;
  receiptEventPackageHistories: ReceiptEventPackageHistoryDataProps;
  adminActionHistories: AdminActionHistoryDataProps;
  transportBookingEvents: TransportBookingEventDataProps;
}

interface DatastoreProviderProps {
  children?: React.ReactNode;
}

const defaultDatastoreConfiguration = {
  maxRecordsToSync: 50000,
  fullSyncInterval: +(process.env.REACT_APP_OVERRIDE_FULL_SYNC_MINUTES ?? 60 * 2),
  syncPageSize: 80
};

const MACHINE_ID_KEY = 'ftl.machineId';
const SESSION_ID_KEY = 'ftl.sessionId';
const LAST_SYNC_START_MS_KEY = 'ftl.lastDataStoreSyncStartMs';

function createStableClientId(prefix: string): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }

  return `${prefix}-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function getStorageItemSafe(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function setStorageItemSafe(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Telemetry should not fail app runtime if storage is unavailable.
  }
}

function getMachineId(): string {
  const existing = getStorageItemSafe(localStorage, MACHINE_ID_KEY);
  if (existing) {
    return existing;
  }

  const generated = createStableClientId('m');
  setStorageItemSafe(localStorage, MACHINE_ID_KEY, generated);
  return generated;
}

function getSessionId(): string {
  const existing = getStorageItemSafe(sessionStorage, SESSION_ID_KEY);
  if (existing) {
    return existing;
  }

  const generated = createStableClientId('s');
  setStorageItemSafe(sessionStorage, SESSION_ID_KEY, generated);
  return generated;
}

function getNavigationType(): string {
  const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return navEntry?.type || 'unknown';
}

const activeHistoricalSsccStatuses = [
  SsccStatus.open,
  SsccStatus.staged,
  SsccStatus.manifested,
  SsccStatus.inTransit,
  SsccStatus.approaching
];

const DatastoreContext = createContext<DatastoreContextProps>({} as DatastoreContextProps);

const useDatastore = () => useContext(DatastoreContext);

const DatastoreProvider = ({ children }: DatastoreProviderProps) => {
  const [isDatastoreReady, setIsDatastoreReady] = useState(false);
  const [isSyncing, setIsSyncing] = useState(true);
  const [newRecordCounts, setNewRecordCounts] = useState<{
    [key: string]: { new: number; updated: number; deleted: number };
  }>({});

  const userDetails = userDetailsData();
  const ssccs = ssccsData();
  const tripSheets = tripSheetsData();
  const locations = locationData();
  const printers = printersData();
  const packageTypes = packageTypeData();
  const qmReasons = qmReasonData();
  const qmIssues = qmIssueData();
  const transportBookingEvents = transportBookingEventData();
  const workflowStatuses = workflowStatusData();
  const removeItemSSCCHistories = removeItemSSCCHistory();
  const receiptEventPackageHistories = receiptEventPackageHistory();
  const adminActionHistories = adminActionHistory();
  const days = +(process.env.SSCC_MANIFEST_FROM_LAST_X_DAYS ?? 60);
  const timePredicate = dayjs().subtract(days, 'day').toISOString();
  const futureDate = dayjs().add(5, 'year').toISOString();

  useEffect(() => {
    const appStartAtMs = Date.now();
    const machineId = getMachineId();
    const sessionId = getSessionId();
    const navigationType = getNavigationType();
    let hasSeenSyncStart = false;
    let sawOfflineSinceLastSyncStart = false;
    let syncCycleSequence = 0;
    let currentSyncCycleId = '';
    let currentSyncCycleCause = 'scheduled-or-maintenance-sync';
    let hasReportedFullSyncInCurrentCycle = false;

    addBreadcrumb('DataStore telemetry context initialized', {
      category: 'datastore.sync.telemetry',
      level: 'info',
      data: {
        machineId,
        sessionId,
        navigationType
      }
    });

    const listener = Hub.listen('datastore', async ({ payload: { event, data } }) => {
      console.info('datastore event', event, data);
      switch (event) {
        case 'ready':
          console.log(event);
          setIsDatastoreReady(true);
          setIsSyncing(false);
          break;
        case 'syncQueriesStarted':
          setIsSyncing(true);
          const nowMs = Date.now();
          const previousSyncStartRaw = getStorageItemSafe(localStorage, LAST_SYNC_START_MS_KEY);
          const previousSyncStartMs = previousSyncStartRaw ? Number(previousSyncStartRaw) : null;
          const previousSyncStartAgoMs =
            previousSyncStartMs && Number.isFinite(previousSyncStartMs) ? nowMs - previousSyncStartMs : null;

          setStorageItemSafe(localStorage, LAST_SYNC_START_MS_KEY, String(nowMs));

          const isReload = navigationType === 'reload';
          let cause = 'scheduled-or-maintenance-sync';

          if (!hasSeenSyncStart) {
            if (isReload) {
              cause = 'startup-after-page-refresh';
            } else {
              cause = 'startup-initial-sync';
            }
          } else if (sawOfflineSinceLastSyncStart) {
            cause = 'reconnect-after-offline';
          }

          hasSeenSyncStart = true;
          sawOfflineSinceLastSyncStart = false;
          syncCycleSequence += 1;
          currentSyncCycleId = `${sessionId}-${syncCycleSequence}`;
          currentSyncCycleCause = cause;
          hasReportedFullSyncInCurrentCycle = false;

          captureMessage('datastore-sync-started', {
            level: 'info',
            context: 'datastore-sync-started',
            tags: {
              sync_event: 'datastore_sync_started',
              machine_id: machineId,
              cause,
              page_navigation_type: navigationType,
              page_reloaded: String(isReload)
            },
            extra: {
              syncCycleId: currentSyncCycleId,
              sessionId,
              appStartAtMs,
              appUptimeMs: nowMs - appStartAtMs,
              fullSyncIntervalMinutes: defaultDatastoreConfiguration.fullSyncInterval,
              previousSyncStartAgoMs,
              datastoreReadyBeforeSyncStart: isDatastoreReady,
              browserOnline: navigator.onLine,
              note_ip_collection: 'enabled via sendDefaultPii'
            }
          });

          addBreadcrumb('DataStore sync started', { category: 'datastore.sync', level: 'info' });
          break;
        case 'syncQueriesReady':
          setIsSyncing(false);
          addBreadcrumb('DataStore sync completed', { category: 'datastore.sync', level: 'info' });
          break;
        case 'outboxMutationFailed':
          console.error('DataStore outbox mutation failed:', data);
          captureError(
            new Error(
              `DataStore outbox mutation failed: ${data?.model?.name || 'unknown'} - ${data?.errorType || 'unknown error'}`
            ),
            'datastore-outbox-mutation-failed'
          );
          break;
        case 'networkStatus':
          if (data?.active === false) {
            console.warn('DataStore network status: offline');
            sawOfflineSinceLastSyncStart = true;
          }
          break;
        case 'modelSynced':
          const {
            counts,
            isFullSync,
            model: { name: modelName }
          } = data;

          if (isFullSync && !hasReportedFullSyncInCurrentCycle) {
            hasReportedFullSyncInCurrentCycle = true;

            captureMessage('datastore-full-sync-detected', {
              level: 'info',
              context: 'datastore-full-sync-detected',
              tags: {
                sync_event: 'datastore_full_sync_detected',
                machine_id: machineId,
                cause: currentSyncCycleCause,
                page_navigation_type: navigationType,
                page_reloaded: String(navigationType === 'reload')
              },
              extra: {
                syncCycleId: currentSyncCycleId,
                sessionId,
                firstModelReportingFullSync: modelName,
                appUptimeMs: Date.now() - appStartAtMs,
                browserOnline: navigator.onLine,
                note_ip_collection: 'enabled via sendDefaultPii'
              }
            });
          }

          if (counts.new > 0 || counts.updated > 0 || counts.deleted > 0) {
            setNewRecordCounts((prev) => ({
              ...prev,
              [modelName]: counts
            }));
          }
          break;
      }
    });

    const syncExpressions = [
      syncExpression(TransportBookingEvent, () => {
        const transportBookingEventStartDate = dayjs().subtract(6, 'month').toISOString();
        console.log('TransportBookingEvent sync expression - filtering records after:', transportBookingEventStartDate);
        return (tbe) => tbe?.createdAt.gt(transportBookingEventStartDate);
      }),
      syncExpression(TripSheet, () => {
        const tripSheetStartDate = dayjs().subtract(6, 'month').toISOString();
        console.log('TripSheet sync expression - filtering records after:', tripSheetStartDate);
        return (ts) => ts?.createdAt.gt(tripSheetStartDate);
      }),
      syncExpression(SSCC, () => {
        return (sscc) =>
          sscc.or((sscc) => [
            sscc?.createdAt.ge(timePredicate),
            ...activeHistoricalSsccStatuses.map((status) =>
              sscc.and((sscc) => [sscc?.createdAt.lt(timePredicate), sscc?.status.eq(status)])
            )
          ]);
      }),
      syncExpression(AdminActionHistory, () => {
        const auditLogStartDate = dayjs().subtract(6, 'month').toISOString();
        console.log('AdminActionHistory sync expression - filtering records after:', auditLogStartDate);
        return (a) => a?.actionTime.gt(auditLogStartDate);
      }),
      syncExpression(ReceiptEventPackageHistory, () => {
        return (r) => r?.actionTime.gt(futureDate);
      }),
      syncExpression(RemoveItemSSCCHistory, () => {
        return (r) => r?.actionTime.gt(futureDate);
      })
    ];

    //Datastore documentation: https://docs.amplify.aws/lib/datastore/getting-started/q/platform/js/
    try {
      DataStore.configure({ ...defaultDatastoreConfiguration, syncExpressions });
      DataStore.start();
    } catch (error) {
      console.error('Failed to initialize DataStore:', error);
      captureError(error, 'datastore-initialize');
      setIsDatastoreReady(false);
      setIsSyncing(true);
    }

    return () => {
      listener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    isDatastoreReady: isDatastoreReady,
    isSyncing: isSyncing,
    syncProgress: newRecordCounts,
    userDetails,
    ssccs,
    tripSheets,
    locations,
    printers,
    packageTypes,
    qmIssues,
    qmReasons,
    workflowStatuses,
    removeItemSSCCHistories,
    receiptEventPackageHistories,
    adminActionHistories,
    transportBookingEvents
  };

  return <DatastoreContext.Provider value={value}>{children}</DatastoreContext.Provider>;
};

export { useDatastore, DatastoreProvider };
